import { useState, useMemo } from "react";
import { useGetAllPreHarvesting } from "../../api/scouting";
import { DataTable } from "../../components/ui/data-table";
import { PreHarvesting } from "../../types/pre-harvesting";
import { AppButton } from "../../components/ui/AppButton";
import { Icons } from "../../assets/images/icons";
import { useTablePrint } from "../../hooks/useTablePrint";
import { ExportButton } from "../../components/ui/ExportButton";
import { Input } from "../../components/ui/input";

const PreHarvestingPage = () => {
  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  /** API */
  const { data: preHarvestingData, isLoading } = useGetAllPreHarvesting({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();

  /** Transform API data */
  const transformedPreHarvestingData = useMemo((): PreHarvesting[] => {
    if (!preHarvestingData?.data?.content) return [];

    return preHarvestingData.data.content.map((item: PreHarvesting) => ({
      id: item.id,
      cropMaturity: item.cropMaturity,
      date: item.date,
      harvestReadiness: item.harvestReadiness,
      recommendations: item.recommendations,
    }));
  }, [preHarvestingData]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  /** Define columns for the DataTable */
  const preHarvestingColumns = [
    {
      key: "cropMaturity",
      title: "Crop Maturity",
      dataIndex: "cropMaturity",
      align: "left" as const,
    },
    {
      key: "date",
      title: "Date",
      dataIndex: "date",
      align: "left" as const,
    },
    {
      key: "harvestReadiness",
      title: "Harvest Readiness",
      dataIndex: "harvestReadiness",
      align: "left" as const,
    },
    {
      key: "recommendations",
      title: "Recommendations",
      dataIndex: "recommendations",
      align: "left" as const,
    },
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between">
        {/* Search - Desktop */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search here..."
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(
                preHarvestingColumns,
                transformedPreHarvestingData,
                "Pre-Harvesting Report"
              )
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>
          <ExportButton
            columns={preHarvestingColumns}
            data={transformedPreHarvestingData}
            fileName="pre-harvesting"
            title="Pre-Harvesting Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: preHarvestingData?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <DataTable<PreHarvesting>
          data={transformedPreHarvestingData}
          columns={preHarvestingColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: preHarvestingData?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No item found"
          rowKey="id"
        />
      </div>
    </>
  );
};

export default PreHarvestingPage;
